import { useQuery } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/auth-fe';

import {
  GetResaleOffers as GetResaleOffersData,
  GetResaleOffersVariables,
} from 'buying/graphql/queries/__generated__/GetResaleOffers';
import {
  PropertyInfo as PropertyInfoData,
  PropertyInfoVariables,
} from 'buying/graphql/queries/__generated__/PropertyInfo';
import { GetResaleOffers } from 'buying/graphql/queries/GetResaleOffers';
import { PropertyInfo } from 'buying/graphql/queries/PropertyInfo';

const fetchPolicy = 'cache-first';

/**
 * This is a hook that fetches all the data needed for the Buy Direct checkout flow.
 * We fetch:
 * - GetResaleOffers to retrieve the customer's offers (using the customer's uuid)
 * - PropertyInfo to retrieve the property's information (using the addressId)
 *
 * @param addressId - the addressId of the property
 * @returns an object containing the customer, property, and resaleOffers
 * @throws {Error} if addressId is not provided
 */
export const useBuyDirect = (addressId?: string | null) => {
  if (!addressId) {
    throw new Error('addressId is required');
  }

  const auth = useAuthAuthenticated();
  const { data: resaleOffers, loading: isResaleOffersLoading } = useQuery<
    GetResaleOffersData,
    GetResaleOffersVariables
  >(GetResaleOffers, {
    variables: {
      customerId: auth.user.customerUuid as string,
    },
    fetchPolicy,
    skip: !auth.user.customerUuid,
  });

  const {
    data: propertyResp,
    loading: isPropertyLoading,
    error,
  } = useQuery<PropertyInfoData, PropertyInfoVariables>(PropertyInfo, {
    variables: {
      addressId,
    },
    fetchPolicy,
  });

  return {
    customer: auth.user,
    property: propertyResp?.property?.property,
    resaleOffers: resaleOffers?.webListingOffer?.listOffers?.offers,
    loading: isResaleOffersLoading || isPropertyLoading,
    error,
  };
};
